import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {css} from "@emotion/react";
import {Col, Container, Row} from "@pg-design/grid";
import {flexAlignCenter, flexDirection, mb, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader";

import {breakpoints} from "../../../common/app/styles/breakpoints";
import {useLandingMarketNewsArticles} from "../hooks/useLandingMarketNewsArticles";
import {
    LANDING_FEATURED_COL_PROPS,
    LANDING_FEATURED_MAX_HEIGHT_PX,
    LANDING_FEATURED_MAX_WIDTH_PX,
    LANDING_MARKET_NEWS_COLORS
} from "./landingMarketNews.constants";
import {getEntryHeroImageUrl} from "./landingMarketNewsArticle";
import {LandingMarketNewsCta} from "./LandingMarketNewsCta";
import {LandingMarketNewsRecommended} from "./LandingMarketNewsRecommended";
import {LandingMarketNewsSliderNav} from "./LandingMarketNewsSliderNav";

import heroBackground from "../assets/landing_market_news_hero.png";

const {panelDark, panelLight} = LANDING_MARKET_NEWS_COLORS;

/**
 * Sekcja skaluje się w proporcji swojego maksimum, więc obrazek trzyma 1.358 (połowa szerokości) na każdej
 * szerokości ekranu, a nie tylko przy 1915px. Pod ten aspect dopasowany jest rendition img_1916x1410.
 */
const FEATURED_SHELL_ASPECT_RATIO = LANDING_FEATURED_MAX_WIDTH_PX / LANDING_FEATURED_MAX_HEIGHT_PX;
const FEATURED_RECOMMENDED_GAP_PX = 40;

export const LandingMarketNewsFeatured = () => {
    const {i18n} = useTranslation();
    const {articles, isLoading, isEmpty} = useLandingMarketNewsArticles();
    const [activeArticleId, setActiveArticleId] = useState<string | null>(null);

    useEffect(() => {
        if (!articles.length) {
            return;
        }

        setActiveArticleId((current) => {
            if (current && articles.some((article) => String(article.id) === current)) {
                return current;
            }

            return String(articles[0].id);
        });
    }, [articles]);

    const activeArticle = useMemo(
        () => articles.find((article) => String(article.id) === activeArticleId) ?? articles[0],
        [activeArticleId, articles]
    );

    const tabs = useMemo(
        () =>
            articles.map((article) => ({
                title: article.title,
                value: String(article.id)
            })),
        [articles]
    );

    const apiHeroImageUrl = getEntryHeroImageUrl(activeArticle);

    if (isEmpty) {
        return null;
    }

    const heroImageUrl = apiHeroImageUrl ?? heroBackground;

    return (
        <section css={heroSection}>
            <div css={featuredShell}>
                {!isLoading && activeArticle && activeArticleId && (
                    <div css={heroBackdrop} aria-hidden>
                        <div css={heroBackdropDark} />
                        <div css={heroBackdropImage}>
                            <img css={[heroImage, heroImageCover]} src={heroImageUrl} alt="" />
                        </div>
                    </div>
                )}

                <Container css={section}>
                    <Row css={featuredRow}>
                        <Col {...LANDING_FEATURED_COL_PROPS} css={featuredCol}>
                            {isLoading ? (
                                <div css={loaderWrapper}>
                                    <Loader />
                                </div>
                            ) : (
                                activeArticle &&
                                activeArticleId && (
                                    <div css={featuredContent}>
                                        <div css={heroBody}>
                                            <LandingMarketNewsCta article={activeArticle} language={i18n.language} />
                                        </div>

                                        <div css={recommendedSlot}>
                                            <div css={desktopRecommended}>
                                                <LandingMarketNewsRecommended
                                                    activeArticleId={activeArticleId}
                                                    onChange={setActiveArticleId}
                                                    tabs={tabs}
                                                />
                                            </div>

                                            <LandingMarketNewsSliderNav
                                                activeArticleId={activeArticleId}
                                                onChange={setActiveArticleId}
                                                tabs={tabs}
                                            />
                                        </div>
                                    </div>
                                )
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </section>
    );
};

const heroSection = css`
    ${w100};
`;

const featuredShell = css`
    position: relative;
    ${w100};
    max-width: ${LANDING_FEATURED_MAX_WIDTH_PX}px;
    margin-inline: auto;
    overflow: hidden;
    ${mb(4)};
    background: ${panelDark};

    @media (min-width: ${breakpoints.sm}) {
        background: transparent;
        aspect-ratio: ${FEATURED_SHELL_ASPECT_RATIO};
    }
`;

const section = css`
    position: relative;
    z-index: 1;
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        height: 100%;
    }
`;

const featuredRow = css`
    justify-content: center;

    @media (min-width: ${breakpoints.sm}) {
        height: 100%;
    }
`;

const featuredCol = css`
    position: relative;
    z-index: 1;

    @media (min-width: ${breakpoints.sm}) {
        height: 100%;
    }
`;

/** Tło na featuredShell – lewa połowa czarna, prawa obrazek (API / placeholder). */
const heroBackdrop = css`
    display: none;

    @media (min-width: ${breakpoints.sm}) {
        position: absolute;
        inset: 0;
        z-index: 0;
        display: flex;
        flex-direction: row;
        overflow: hidden;
        pointer-events: none;
    }
`;

const heroBackdropDark = css`
    flex: 1 1 50%;
    min-width: 0;
    background: ${panelDark};
`;

const heroBackdropImage = css`
    flex: 1 1 50%;
    min-width: 0;
    overflow: hidden;
    background: ${panelLight};
`;

const featuredContent = css`
    position: relative;
    z-index: 1;
    display: grid;
    ${w100};
    min-height: 0;

    @media (min-width: ${breakpoints.sm}) {
        grid-template-rows: 1fr auto;
        gap: ${FEATURED_RECOMMENDED_GAP_PX}px;
        height: 100%;
        justify-content: center;
        align-items: end;
    }
`;

const heroImage = css`
    display: block;
    ${w100};
    height: 100%;
`;

/** img_1916x1410 to dokładnie 2x slotu 957.5x705 – crop zjada tylko biały margines okładki, nie treść. */
const heroImageCover = css`
    object-fit: cover;
    object-position: center;
`;

const heroBody = css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    min-width: 0;
    min-height: 0;

    @media (min-width: ${breakpoints.sm}) {
        max-width: 50%;
        overflow: hidden;
    }
`;

const recommendedSlot = css`
    min-width: 0;
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        ${mb(4)};
    }

    @media (min-width: ${breakpoints.xl}) {
        ${mb(8)};
    }
`;

const desktopRecommended = css`
    display: none;

    @media (min-width: ${breakpoints.sm}) {
        display: block;
    }
`;

const loaderWrapper = css`
    position: relative;
    z-index: 1;
    ${flexAlignCenter};
    ${flexDirection("column")};
    min-height: 200px;

    @media (min-width: ${breakpoints.sm}) {
        height: 100%;
    }
`;
